// ============================================================================
// caseStatusWidget — materialises the reusable Case Status Update widget
// ============================================================================
// A section authored as `{ type: 'case_status_update', case_status_update: {…} }`
// expands into a `render_as: 'case_status'` section drawn by <CaseStatusSection>:
// a card-based proceedings log (one row per update) plus — when `award` is on —
// an inline Award Details block that appears once any row's status equals the
// `awarded_value`. Persisted in the state-data JSONB under `status_updates`
// (array) + flat `award_*` keys. Reusable across any "repeat until X" phase
// (arbitration hearings, court postings, follow-ups) — only the config differs.

import type {
  FieldSpec,
  GridFieldSpec,
  GridRowFieldSpec,
  SectionSpec,
  CaseStatusUpdateConfig,
} from 'api/types/formSpec.types';

const DEFAULT_AWARDED_VALUE = 'AWARDED';

// The proceedings-log grid (one row per hearing / posting). Field keys are
// stable; labels are config-driven so the same widget reads "Hearings" in
// arbitration and "Postings" elsewhere.
function statusUpdatesGrid(cfg: CaseStatusUpdateConfig): GridFieldSpec {
  const rowFields: GridRowFieldSpec[] = [
    { key: 'update_date', type: 'date', label: cfg.date_label ?? 'Date', required: true },
    { key: 'outcome_summary', type: 'textarea', label: cfg.summary_label ?? 'Outcome Summary', max_length: 3000 },
    {
      key: 'next_posting', type: 'select', label: cfg.status_label ?? 'Next Posting',
      options: cfg.status_options ?? [
        { value: 'FOR_EVIDENCE', label: 'For Evidence' },
        { value: 'FOR_ARGUMENTS', label: 'For Arguments' },
        { value: 'FOR_ORDERS', label: 'For Orders' },
        { value: 'ADJOURNED', label: 'Adjourned' },
        { value: DEFAULT_AWARDED_VALUE, label: 'Awarded' },
        { value: 'OTHER', label: 'Other' },
      ],
    },
    { key: 'next_hearing_date', type: 'date', label: cfg.next_date_label ?? 'Next Hearing Date' },
    ...(cfg.extra_fields ?? []),
  ];

  return {
    key: 'status_updates',
    type: 'grid',
    label: 'Updates',
    min_rows: 0,
    max_rows: 200,
    add_label: 'Add Update',
    empty_message: 'No updates yet. Add one for each hearing / posting.',
    row_stepper: ['update_date', 'next_posting', 'next_hearing_date'],
    row_fields: rowFields,
  } as unknown as GridFieldSpec;
}

// Case-level Award record — flat scalar keys, shown by <CaseStatusSection> only
// when the proceedings reach the awarded state. Kept in `fields[]` so values
// plumbing + snapshot denorm still run; the component controls visibility.
export function awardFields(): FieldSpec[] {
  return [
    { key: 'award_date', type: 'date', label: 'Award Date', denorm_to: 'event_date' } as FieldSpec,
    { key: 'award_dispatch_date', type: 'date', label: 'Award Dispatch Date' } as FieldSpec,
    { key: 'award_amount', type: 'number', label: 'Award Amount (Rs.)', min: 0, denorm_to: 'amount_primary' } as unknown as FieldSpec,
    { key: 'award_remarks', type: 'textarea', label: 'Award Remarks', max_length: 3000 } as unknown as FieldSpec,
    {
      key: 'next_action_type', type: 'select', label: 'Next Action', denorm_to: 'category_value',
      options: [
        { value: 'CLOSE', label: 'Close - award satisfied / no further action' },
        { value: 'EXECUTION', label: 'File Execution Petition' },
        { value: 'OTHER', label: 'Other recovery action' },
      ],
    } as unknown as FieldSpec,
  ];
}

/** Expand a case-status-update section into a `render_as: 'case_status'` section. */
export function expandCaseStatusSection(section: SectionSpec): SectionSpec {
  const cfg: CaseStatusUpdateConfig = section.case_status_update ?? {};
  const fields: FieldSpec[] = [statusUpdatesGrid(cfg)];
  if (cfg.award) fields.push(...awardFields());
  return {
    key: section.key,
    title: cfg.title ?? section.title ?? 'Case Status Updates',
    description: cfg.description ?? section.description,
    tab_label: section.tab_label,
    layout: 'auto',
    fields,
    render_as: 'case_status',
    case_status_update: cfg,
  };
}

export const isCaseStatusSection = (s: SectionSpec): boolean =>
  s.type === 'case_status_update' || s.render_as === 'case_status' || !!s.case_status_update;
